import type {
    ApplicationType,
    FormDataType,
    FormElementType,
} from './types';

/**
 * Leser en dot-path ut av et objekt — `"brreg.postadresse.kommune"` plukker
 * fram `obj.brreg.postadresse.kommune`. Returnerer `undefined` hvis noen
 * mellomledd ikke finnes.
 */
export function resolveByPath(obj: unknown, path: string): unknown {
    if (!obj || !path) return undefined;
    const parts = path.split('.');
    let cur: any = obj;
    for (const p of parts) {
        if (cur === null || cur === undefined) return undefined;
        cur = cur[p];
    }
    return cur;
}

/**
 * Returnerer første ikke-tomme verdi som `autoupdate`-konfigurasjonen
 * resolverer til mot `formData`. `autoupdate` kan være en enkelt path eller
 * et prioritert array av paths (første treff vinner).
 */
export function getAutoupdateValue(
    autoupdate: string | string[] | undefined,
    formData: FormDataType
): string | undefined {
    if (!autoupdate) return undefined;
    const paths = Array.isArray(autoupdate) ? autoupdate : [autoupdate];
    for (const path of paths) {
        const value = resolveByPath(formData, path);
        if (value === null || value === undefined) continue;
        if (typeof value === 'string' && value.trim() === '') continue;
        if (typeof value === 'object') {
            // ikke kopier objekter/arrays — bruk første verdi, hopp over tomme lister
            const first = Array.isArray(value) ? value[0] : undefined;
            if (first === null || first === undefined || String(first).trim() === '') continue;
            return String(first);
        }
        return String(value);
    }
    return undefined;
}

/**
 * En "tom" verdi i kontekst av autoupdate. Vi setter kun ny verdi når feltet
 * er tomt — brukerens egne inputs skal aldri overskrives.
 */
export function isEmptyValue(value: unknown): boolean {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    if (Array.isArray(value) && value.length === 0) return true;
    return false;
}

/**
 * Itererer alle elementer (inkl. radiogroupdepending innerElements) og
 * returnerer de som har en `defaultValue` (predefinert verdi) satt. Brukes
 * til å seede formData — bl.a. sammen med `hidden`-felt som bare skal ha én
 * mulig verdi (f.eks. `category`/`subtype`).
 */
export function collectDefaultValueElements(
    structure: { sections?: any[] } | null | undefined
): FormElementType[] {
    if (!structure?.sections) return [];
    const out: FormElementType[] = [];

    for (const section of structure.sections) {
        const forms = (section as any).forms;
        if (!Array.isArray(forms)) continue;
        for (const form of forms) {
            for (const el of form.elements ?? []) {
                const dv = (el as any).defaultValue;
                if (dv !== undefined && dv !== '') out.push(el);
                if ((el as any).type === 'radiogroupdepending' && Array.isArray((el as any).depends)) {
                    for (const dep of (el as any).depends) {
                        for (const inner of dep.innerElements ?? []) {
                            const idv = (inner as any).defaultValue;
                            if (idv !== undefined && idv !== '') out.push(inner);
                        }
                    }
                }
            }
        }
    }

    return out;
}

/**
 * Itererer alle elementer i hele applikasjonen (inkl. radiogroupdepending
 * sine innerElements) og returnerer de som har `autoupdate` satt.
 */
export function collectAutoupdateElements(
    application: ApplicationType | null | undefined
): FormElementType[] {
    if (!application) return [];
    const out: FormElementType[] = [];

    for (const section of application.sections ?? []) {
        const forms = (section as any).forms;
        if (!Array.isArray(forms)) continue;
        for (const form of forms) {
            for (const el of form.elements ?? []) {
                if ((el as any).autoupdate) out.push(el);
                if ((el as any).type === 'radiogroupdepending' && Array.isArray((el as any).depends)) {
                    for (const dep of (el as any).depends) {
                        for (const inner of dep.innerElements ?? []) {
                            if ((inner as any).autoupdate) out.push(inner);
                        }
                    }
                }
            }
        }
    }

    return out;
}
